import * as React from "react";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";

import { Box, Avatar, Badge } from "@mui/material";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Profile from "./Profile.js";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ControlPanel from "./ControlPanel";

// Add MUI Dialog imports
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

export default function Clock({
  time = 60,
  path = "",
  width = "150px",
  height = "150px",
  marginTop = "0px",
  marginRight = "0px",
  marginLeft = "0px",
  loop = true,
  autoplay = true,
  avatars,
  position,
  userData,
  totalMoves,
  totalGuesses,
  movesGuesses,
  onRequestHint,
  game,
  // for control panel
  boardWidth,
  playerColorFromServer,
  gameIdFromServer,
  onCaptureBoard,
  setIsViewer,
  gameOver,
  onAccordionChange,
  onGameHistoryChange,
  getMoveHistory,
  aiLevel,
  gameInfo,
  winnerColor,
  learnMode,
  evaluation,
  showGameHistory,
  topSubscription,
}) {
  const { t } = useTranslation();
  const isMobile = window.innerWidth <= 600;

  const formatTime = (seconds) => {
    // Make sure seconds is a number
    const totalSeconds = Number(seconds);

    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const paddedHrs = hrs.toString().padStart(2, "0");
    const paddedMins = mins.toString().padStart(2, "0");
    const paddedSecs = secs.toString().padStart(2, "0");

    // Show HH:MM:SS only if over 30 minutes
    if (totalSeconds >= 1800) {
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    } else {
      return `${paddedMins}:${paddedSecs}`;
    }
  };

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedAvatar, setSelectedAvatar] = React.useState(null);

  // --- NEW STATE FOR HINT DIALOG ---
  const [hintDialogOpen, setHintDialogOpen] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(
    localStorage.getItem("dontShowHintDialog") === "true"
  );
  // --- END NEW STATE ---

  const getMarginRight = () => {
    if (position === "bottom") {
      if (isMobile) {
        if (game?.gameInfo?.gameTeamType !== "team_3") {
          return "20px"; // Adjusted for one-on-one games
        }
        return "130px";
      } else {
        if (game?.gameInfo?.gameTeamType !== "team_3") {
          return "20px"; // Adjusted for one-on-one games
        }
        return "130px";
      }
    }
  };

  const handleAvatarClick = (avatar) => {
    setSelectedAvatar(avatar);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAvatar(null);
  };

  const handleAddToFavorite = () => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "add_to_favorite";
    generalMessage.user.userName = selectedAvatar.userName;
    sendMessage(generalMessage);
    handleDialogClose();
  };

  // --- NEW HINT HANDLERS ---
  const handleHintClick = () => {
    if (dontShowAgain) {
      onRequestHint();
    } else {
      setHintDialogOpen(true);
    }
  };

  const handleHintConfirm = () => {
    if (dontShowAgain) {
      localStorage.setItem("dontShowHintDialog", "true");
    }
    setHintDialogOpen(false);
    onRequestHint();
  };

  const handleHintCancel = () => {
    setHintDialogOpen(false);
  };
  // --- END HINT HANDLERS ---

  React.useEffect(() => {
    if (avatars?.length > 0) {
      avatars.forEach((avatar) => {});
    }
  }, [avatars]);

  // ✅ Only render ControlPanel when game is over
  if (gameOver) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: 2,
          marginTop: isMobile ? "-120px" : "-120px", // Adjusted for mobile
        }}
      >
        <ControlPanel
          boardWidth={boardWidth}
          playerColorFromServer={playerColorFromServer}
          gameIdFromServer={gameIdFromServer}
          onCaptureBoard={onCaptureBoard}
          setIsViewer={setIsViewer}
          gameOver={gameOver}
          onGameHistoryChange={onGameHistoryChange}
          getMoveHistory={getMoveHistory}
          aiLevel={aiLevel}
          gameInfo={gameInfo}
          evaluation={evaluation}
          showGameHistory={showGameHistory}
          topSubscription={topSubscription}
          userData={userData}
          backgroundColor="#4a707d"
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row", // side by side
        justifyContent: "center",
        alignItems: "flex-start", // align top
        gap: 2,
        flexWrap: "wrap", // responsive stacking on mobile
        padding: 1,
        zIndex: 100,
      }}
    >
      {/* Left: Clock + Profile Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "fit-content", // prevent dynamic expansion
          minHeight: "170px", // set based on your content (adjust as needed)
          overflow: "visible", // allow ControlPanel to overflow without pushing content
          position: "relative",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="center"
          alignItems="center"
          sx={{
            border: "4px solid #ccc",
            borderRadius: "15px",
            padding: "0px",
            boxShadow: "0 15px 15px black",
            zIndex: 20,
            position: "relative",
          }}
        >
          {/* Timer Animation */}

          {/* Time Display */}
          {!gameOver && (
            <div
              style={{
                marginTop: "-3px",
                marginRight: isMobile ? "40px" : "40px",
                fontSize: "22px",
                color: "black",
                fontWeight: "bold",
                textAlign: "center",
                padding: "5px",
                zIndex: 100,
                border: "4px solid #ccc",
                borderRadius: "15px",
                boxShadow: "0 15px 15px black",
              }}
            >
              {t("Your_Time")} {formatTime(time)}
            </div>
          )}

          {/* Right: Control Panel */}
          {position === "bottom" && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                top: 0,
                zIndex: 50,
              }}
            >
              {gameOver === false &&
                avatars &&
                avatars.length > 0 &&
                (() => {
                  // Find the current user's avatar object
                  const currentAvatar = avatars.find(
                    (a) => a?.id === userData?.id
                  );
                  return (
                    <Tooltip title={t("Use a hint")}>
                      <Badge
                        badgeContent={currentAvatar?.hints ?? 0}
                        color="secondary"
                        overlap="circular"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.75rem",
                            height: "20px",
                            minWidth: "20px",
                            top: 10,
                            right: 10,
                          },
                        }}
                      >
                        <IconButton
                          onClick={handleHintClick}
                          sx={{
                            color: "red",
                            backgroundColor: "gold",
                            border: "5px solid #ccc",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          <LightbulbOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                  );
                })()}
            </Box>
          )}
          {/* Avatars */}
          {avatars?.map((avatar, index) => {
            const isCurrentUser = avatar?.id === userData?.id;
            return (
              <Badge
                key={index}
                badgeContent={
                  avatar?.moveStatus === "waiting" ? "" : ""
                }
                color={avatar?.moveStatus === "waiting" ? "error" : "primary"}
                sx={{
                  zIndex: 100,
                  "& .MuiBadge-badge": {
                    top: -2,
                    right: isCurrentUser ? 5 : 5,
                  },
                }}
              >
                <div
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {!gameOver && (
                    <Avatar
                      alt={avatar?.name}
                      src={avatar?.url}
                      style={{
                        border: isCurrentUser
                          ? "4px solid green"
                          : "4px solid #ccc",
                        borderRadius: "50px",
                        boxShadow: "0 15px 15px black",
                        width: "50px",
                        height: "50px",
                        cursor: "pointer",
                        marginTop: "-5px",
                        zIndex: 0,
                      }}
                      onClick={() => handleAvatarClick(avatar)}
                    />
                  )}
                </div>
              </Badge>
            );
          })}
        </Stack>

        {/* Control Panel */}
        {position === "bottom" && (
          <Box
              sx={{
                position: "absolute",
                marginTop: isMobile ? "2px" : "7px",
                marginRight: getMarginRight(),
                zIndex: 50,
              }}
            >
            <ControlPanel
              boardWidth={boardWidth}
              playerColorFromServer={playerColorFromServer}
              gameIdFromServer={gameIdFromServer}
              onCaptureBoard={onCaptureBoard}
              setIsViewer={setIsViewer}
              gameOver={gameOver}
              onGameHistoryChange={onGameHistoryChange}
              getMoveHistory={getMoveHistory}
              aiLevel={aiLevel}
              gameInfo={gameInfo}
              evaluation={evaluation}
              showGameHistory={showGameHistory}
              topSubscription={topSubscription}
              userData={userData}
              backgroundColor="#4a707d"
            />
          </Box>
        )}

        {/* Profile Dialog */}
        <Profile
          open={dialogOpen}
          onClose={handleDialogClose}
          avatar={selectedAvatar}
          onAddToFavorite={handleAddToFavorite}
          userData={userData}
          t={t}
        />

        {/* --- HINT CONFIRMATION DIALOG --- */}
        <Dialog open={hintDialogOpen} onClose={handleHintCancel}>
          <DialogTitle>{t("Use a hint?")}</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: 8 }}>
              {t("Do you want to use a hint?")}
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => {
                    setDontShowAgain(e.target.checked);
                    localStorage.setItem("dontShowHintDialog", e.target.checked);
                  }}
                />
              }
              label={t("Don't show again – just make a hint")}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleHintCancel}>{t("No")}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleHintConfirm}
            >
              {t("Yes")}
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- END HINT DIALOG --- */}

      </Box>
    </Box>
  );
}
